import { Box, Button as ChakraButton } from '@chakra-ui/react';
import { GetIcon } from '../../../components/icons';
import { FREE_SHIPPING_LABEL } from '../../../hooks/useIsFreeShipping';
import { Text } from '../../typography';
import type { IconMap } from '../../../components/icons/Icon.map';

export interface SelectButtonProps {
    id: string;
    value?: string;
    price?: string | number;
    description?: string | number;
    title: string;
    icon: keyof typeof IconMap;
    isActive?: boolean;
    isDisabled?: boolean;
    onClick?: (value?: string) => void;
}

export const SelectButton = ({
    id,
    title,
    icon,
    value,
    isActive = false,
    price,
    isDisabled = false,
    description = '',
    onClick,
}: SelectButtonProps): React.ReactElement => (
    <ChakraButton
        data-testid={id}
        variant="select-button"
        isActive={isActive}
        isDisabled={isDisabled}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        w="100%"
        minW={{ base: '343px', sm: '230px', md: 'unset' }}
        alignItems="start"
        p="0.5rem"
        _disabled={{ opacity: 0.5 }}
        onClick={() => onClick && onClick(value)}
        _active={{
            outlineOffset: '-2px',
            outline: isDisabled ? 'none' : '2px solid',
        }}
        className="space-y-1"
    >
        <Box
            className="space-x-1"
            w="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            position="relative"
        >
            <Box
                className="xs:max-sm:space-x-1 sm:max-md:space-y-1 md:max-lg:space-x-1 lg:space-y-1"
                w="100%"
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                flexWrap={{ sm: 'wrap', md: 'nowrap', lg: 'wrap' }}
            >
                <GetIcon id={`${id}-icon`} name={icon} size="lg" />
                <Box w={{ sm: '100%' }} textAlign="left">
                    <Text id={`${id}-title`} style="bold" size="small">
                        {title}
                    </Text>
                </Box>
            </Box>
            {price && (
                <Box py={1} position={{ lg: 'absolute', xl: 'static' }} top="0" right="0">
                    <Text
                        id={`${id}-price`}
                        style="bold"
                        color={price === FREE_SHIPPING_LABEL ? 'slt-primary-green.1000' : 'unset'}
                    >
                        {typeof price === 'number' ? `$${price}` : price}
                    </Text>
                </Box>
            )}
        </Box>
        {description && (
            <Box w="100%" textAlign="left">
                <Text id={`${id}-description`} size="small" style="regular">
                    {description}
                </Text>
            </Box>
        )}
    </ChakraButton>
);
